import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["arrowRef", "popoverId", "singleGroupId", "captureOptions", "onVisibleChange"];

/**
 * @file usePopover
 */
import { useCallback, useEffect, useRef } from 'react';
import { useElementBoundary } from '@tencent/glue-react-boundary';
import { useCombineRef, useRefCallback, useSingleton, useUpdate } from '@tencent/glue-react-use';
import { Popover } from '@tencent/glue-model-popper';
import { useLayer } from '@tencent/glue-react-layer';
import { usePopperInstance } from '../context/Popper';
import { assign } from '@tencent/glue-util-helper';
export function usePopover(options) {
  var arrowRef = options.arrowRef,
      popoverId = options.popoverId,
      singleGroupId = options.singleGroupId,
      _options$captureOptio = options.captureOptions,
      captureOptions = _options$captureOptio === void 0 ? {} : _options$captureOptio,
      onVisibleChange = options.onVisibleChange,
      popperOptions = _objectWithoutProperties(options, _excluded);

  var update = useUpdate();
  var containerRef = useLayer();
  var onVisibleChangeCallback = useRefCallback(onVisibleChange);
  var popper = usePopperInstance();
  var popover = useSingleton(function () {
    return new Popover({
      popper: popper,
      singleGroupId: singleGroupId,
      id: popoverId
    });
  });
  var popoverStyleRef = useRef(null);
  var arrowStyleRef = useRef(null);
  var popoverRef = useRef(null);
  popover.updatePopoverOptions(popperOptions);

  var _useElementBoundary = useElementBoundary(function () {
    return popover.isShow();
  }, function (rect) {
    return popover.updateRect(rect);
  }, captureOptions),
      _useElementBoundary2 = _slicedToArray(_useElementBoundary, 1),
      ref = _useElementBoundary2[0];

  var reference = useCombineRef(ref, popoverRef, function (node) {
    popover.updatePopover(node);

    if (node) {
      node.setAttribute('data-rc-id', "popover___".concat(popover.getId()));
    }
  });
  useEffect(function () {
    popover.updateContainer(containerRef.current);
  });
  useEffect(function () {
    var onPopoverStyleChange = function onPopoverStyleChange(styles) {
      // 位置不确定时，抵消样式变动
      if (popoverStyleRef.current && isUncertainPosition(popover)) {
        styles = popoverStyleRef.current;
      }

      if (popoverRef.current) {
        assign(popoverRef.current.style, styles);
      }
    };

    var onArrowInfoChange = function onArrowInfoChange() {
      var styles = popover.getArrowStyle(); // 位置不确定时，抵消样式变动

      if (arrowStyleRef.current && isUncertainPosition(popover)) {
        styles = arrowStyleRef.current;
      }

      if (arrowRef.current) {
        assign(arrowRef.current.style, styles);
      }
    };

    popover.addListener('popover-show', onVisibleChangeCallback);
    popover.addListener('popover-style', onPopoverStyleChange);
    popover.addListener('arrow-info-change', onArrowInfoChange);
    popover.addListener('last-trigger-update', update);
    popover.addListener('touch-change', update);
    return function () {
      popover.removeListener('popover-show', onVisibleChangeCallback);
      popover.removeListener('popover-style', onPopoverStyleChange);
      popover.removeListener('arrow-info-change', onArrowInfoChange);
      popover.removeListener('last-trigger-update', update);
      popover.removeListener('touch-change', update);
      popover.dispose();
    };
  }, []);
  var getContainer = useCallback(function () {
    return containerRef.current;
  }, []);
  var onBlurCapture = useCallback(function () {
    return popover.onPopperBlur();
  }, []);
  var onMouseLeave = useCallback(function () {
    return popover.onPopperMouseLeave();
  }, []);
  var onMouseEnter = useCallback(function () {
    return popover.onPopperMouseEnter();
  }, []);
  var onFocusCapture = useCallback(function () {
    return popover.onPopperFocus();
  }, []); // 如果位置不确定会使用上一次的 style 值

  if (!isUncertainPosition(popover) || !arrowStyleRef.current || !popoverStyleRef.current) {
    popoverStyleRef.current = popover.getPopoverStyle();
    arrowStyleRef.current = popover.getArrowStyle();
  }

  return [reference, {
    popover: popover,
    arrowInfo: popover.arrowInfo,
    data: popover.getLastTrigger() ? popover.getTriggerData() : null,
    placement: popover.getRealPlacement(),
    getContainer: getContainer,
    popoverStyle: popoverStyleRef.current,
    onBlurCapture: onBlurCapture,
    onFocusCapture: onFocusCapture,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave
  }];
}

function isUncertainPosition(popover) {
  var refRect = popover.getRefRect();
  return !(refRect.width || refRect.height || refRect.top || refRect.left);
}